import React from 'react';
import { FiArrowUpRight, FiEye, FiZap } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';

type FocusItem = {
  key: 'vision' | 'mission';
  titleKey: string;
  subtitleKey: string;
  descriptionKey: string;
  icon: React.ComponentType<{ className?: string }>;
};

const items: FocusItem[] = [
  {
    key: 'vision',
    titleKey: 'aboutPageSection.visionMission.items.vision.title',
    subtitleKey: 'aboutPageSection.visionMission.items.vision.subtitle',
    descriptionKey: 'aboutPageSection.visionMission.items.vision.description',
    icon: FiEye,
  },
  {
    key: 'mission',
    titleKey: 'aboutPageSection.visionMission.items.mission.title',
    subtitleKey: 'aboutPageSection.visionMission.items.mission.subtitle',
    descriptionKey: 'aboutPageSection.visionMission.items.mission.description',
    icon: FiZap,
  },
];

const AboutVisionMission: React.FC = () => {
  const { t } = useTranslation();
  const [activeKey, setActiveKey] = React.useState<FocusItem['key']>('vision');
  const activeItem = items.find((item) => item.key === activeKey) || items[0];

  return (
    <section className="py-24 bg-[#F7F8FA]">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-14">
          <p className="text-teal-600 text-xs font-bold uppercase tracking-[0.2em] mb-3">{t('aboutPageSection.visionMission.eyebrow')}</p>
          <h2 className="text-3xl font-bold text-[#0F172A] tracking-tight">{t('aboutPageSection.visionMission.title')}</h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          <div className="lg:col-span-5 space-y-3">
            {items.map((item) => {
              const isActive = item.key === activeKey;
              const Icon = item.icon;
              return (
                <button
                  key={item.key}
                  type="button"
                  onMouseEnter={() => setActiveKey(item.key)}
                  onFocus={() => setActiveKey(item.key)}
                  onClick={() => setActiveKey(item.key)}
                  className={`w-full text-left rounded-xl border px-4 py-4 transition-all duration-200 ${
                    isActive
                      ? 'border-teal-300 bg-white shadow-sm'
                      : 'border-[#E2E8F0] bg-white/70 hover:border-teal-200 hover:bg-white'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className={`inline-flex h-9 w-9 items-center justify-center rounded-lg ${isActive ? 'bg-teal-50 text-teal-700' : 'bg-slate-100 text-slate-600'}`}>
                        <Icon className="h-4 w-4" />
                      </span>
                      <div>
                        <p className="text-sm font-bold text-[#0F172A]">{t(item.titleKey)}</p>
                        <p className="text-[11px] uppercase tracking-[0.12em] text-[#94A3B8]">{t(item.subtitleKey)}</p>
                      </div>
                    </div>
                    <FiArrowUpRight className={`h-4 w-4 ${isActive ? 'text-teal-600' : 'text-slate-400'}`} />
                  </div>
                </button>
              );
            })}
          </div>

          <article className="lg:col-span-7 rounded-2xl border border-[#E2E8F0] bg-white p-7 sm:p-8">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-teal-50 text-teal-700">
                <activeItem.icon className="h-5 w-5" />
              </span>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-teal-600">{t(activeItem.subtitleKey)}</p>
                <h3 className="text-2xl font-bold text-[#0F172A] tracking-tight">{t(activeItem.titleKey)}</h3>
              </div>
            </div>
            <p className="mt-5 text-[#64748B] leading-relaxed text-base">{t(activeItem.descriptionKey)}</p>
          </article>
        </div>
      </div>
    </section>
  );
};

export default AboutVisionMission;
